import { supabase } from './supabase';
import { Employee } from '../types';

export const employeeService = {
  // Get all employees
  async getAll(): Promise<Employee[]> {
    const { data, error } = await supabase
      .from('employees')
      .select('*')
      .order('last_name', { ascending: true });

    if (error) {
      console.error('Error fetching employees:', error);
      if (error.code === '42P01') {
        throw new Error('Database tables not found. Please run the setup script first.');
      }
      return [];
    }

    return data as Employee[];
  },

  // Get employee by ID
  async getById(id: string): Promise<Employee | null> {
    const { data, error } = await supabase
      .from('employees')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error(`Error fetching employee with ID ${id}:`, error);
      return null;
    }

    return data as Employee;
  },

  // Create a new employee
  async create(employee: Omit<Employee, 'id' | 'created_at' | 'updated_at'>): Promise<Employee | null> {
    const { data, error } = await supabase
      .from('employees')
      .insert([{ ...employee }])
      .select()
      .single();

    if (error) {
      console.error('Error creating employee:', error);
      return null;
    }

    return data as Employee;
  },

  // Update an employee
  async update(id: string, employee: Partial<Employee>): Promise<Employee | null> {
    console.log(`Attempting to update employee ${id} with data:`, employee);
    
    const updateData = { ...employee, updated_at: new Date().toISOString() };
    console.log('Final update data:', updateData);
    
    const { data, error } = await supabase
      .from('employees')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error(`Error updating employee with ID ${id}:`, error);
      console.error('Error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      return null;
    }

    console.log('Employee update successful:', data);
    return data as Employee;
  },

  // Delete an employee
  async delete(id: string): Promise<boolean> {
    try {
      // First, check if employee has related records that would prevent deletion
      const { data: relatedRecords, error: checkError } = await supabase
        .rpc('check_employee_dependencies', { employee_id: id });

      if (checkError) {
        console.warn('Could not check dependencies, proceeding with deletion:', checkError);
      }

      // Attempt to delete the employee
      const { error } = await supabase
        .from('employees')
        .delete()
        .eq('id', id);

      if (error) {
        console.error(`Error deleting employee with ID ${id}:`, error);
        
        // Provide more specific error messages based on the error code
        if (error.code === '23503') {
          throw new Error('Cannot delete employee: This employee has related records (payroll, attendance, leave requests, etc.). Please remove or reassign these records first.');
        } else if (error.code === '42501') {
          throw new Error('Permission denied: You do not have permission to delete employees.');
        } else {
          throw new Error(`Database error: ${error.message}`);
        }
      }

      return true;
    } catch (error) {
      console.error(`Error deleting employee with ID ${id}:`, error);
      throw error; // Re-throw to be handled by the calling component
    }
  },

  // Search employees
  async search(query: string): Promise<Employee[]> {
    const { data, error } = await supabase
      .from('employees')
      .select('*')
      .or(`first_name.ilike.%${query}%,last_name.ilike.%${query}%,email.ilike.%${query}%,position.ilike.%${query}%,department.ilike.%${query}%`)
      .order('last_name', { ascending: true });

    if (error) {
      console.error('Error searching employees:', error);
      return [];
    }

    return data as Employee[];
  },

  // Get employees by department
  async getByDepartment(department: string): Promise<Employee[]> {
    const { data, error } = await supabase
      .from('employees')
      .select('*')
      .eq('department', department)
      .order('last_name', { ascending: true });

    if (error) {
      console.error(`Error fetching employees from department ${department}:`, error);
      return [];
    }

    return data as Employee[];
  },

  // Get all active employees for export
  async getActiveEmployeesForExport(): Promise<Employee[]> {
    const { data, error } = await supabase
      .from('employees')
      .select('*')
      .eq('status', 'active')
      .order('last_name', { ascending: true });

    if (error) {
      console.error('Error fetching active employees for export:', error);
      if (error.code === '42P01') {
        throw new Error('Database tables not found. Please run the setup script first.');
      }
      return [];
    }

    return data as Employee[];
  },
};